import { Stack, Tabs } from '@mantine/core';
import { spotlight } from '@mantine/spotlight';
import { trackEvent } from '@repo/common/analytics';
import type { EmptyStateButtons } from '@repo/common/components/NewEmptyState/EmptyState';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import TabsList from '@repo/common/components/TabsList';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { useKeyPress } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuthUser, useQuestionsEnabled } from '../../api';
import { useTeamEmptyState } from '../../api/hooks/team/emptyStates';
import { useTemplateList } from '../../api/hooks/templates';
import { entityModalStore } from '../../components/EntityModal/store';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { GLOBAL_SHORTCUTS } from '../../constants/shortcuts';
import { QuestionsOverviewTabController } from '../../features/Overview/QuestionsOverviewTabController';
import type { MetricCardType } from '../../features/Overview/types';
import { useEntityBreadcrumbTeam } from '../../hooks/useEntityBreadcrumbs';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import { TeamQuestionsTable } from './TeamQuestionsTable';

const METRIC_FILTERS: Partial<
	Record<
		MetricCardType,
		Array<{
			value: string;
			operator: string;
			filterType: string;
		}>
	>
> = {
	Open: [
		{
			value: 'UNANSWERED',
			operator: 'is',
			filterType: 'question_status',
		},
	],
	InProgress: [
		{
			value: 'IN_PROGRESS',
			operator: 'is',
			filterType: 'question_status',
		},
	],
	InReview: [
		{
			value: 'IN_REVIEW',
			operator: 'is',
			filterType: 'question_status',
		},
	],
	Answered: [
		{
			value: 'ANSWERED',
			operator: 'is',
			filterType: 'question_status',
		},
	],
	Cancelled: [
		{
			value: 'CANCELLED',
			operator: 'is',
			filterType: 'question_status',
		},
	],
};

const ALLOWED_ROLES = [
	UserRole.ADMIN,
	UserRole.EDITOR,
	UserRole.VIEWER,
	UserRole.GUEST,
];

function TeamQuestionsPage() {
	const navigate = useNavigate();
	const { classes } = useStyles();
	const { teamId } = useParams();
	const { user, workspace } = useAuthUser();
	const { questionsOverview } = useFeatureFlags();
	const questionsEnabled = useQuestionsEnabled();

	const emptyState = useTeamEmptyState(teamId as string, 'question');

	const tabs = useMemo(
		() => [
			{ value: `/teams/${teamId}/questions/overview`, label: 'Overview' },
			{ value: `/teams/${teamId}/questions`, label: 'Questions' },
		],
		[teamId]
	);

	const activeTabKey = `questions-tab-${teamId}`;

	const [activeTab, setActiveTab] = useState<string>(() => {
		const storedTab = localStorage.getItem(activeTabKey);
		const urlParams = new URLSearchParams(window.location.search);
		// If there are filters in the URL, default to requests tab
		if (urlParams.has('filters')) {
			return `/teams/${teamId}/questions`;
		}
		return storedTab || `/teams/${teamId}/questions/overview`;
	});

	const handleTabChange = (value: string): void => {
		if (tabs.some((tab) => tab.value === value)) {
			setActiveTab(value);
			localStorage.setItem(activeTabKey, value);
			navigate(value);
		}
		trackEvent(
			'questions/tabs/click',
			{
				tab: value,
			},
			user,
			workspace!
		);
	};

	const { setTeamBreadcrumb } = useEntityBreadcrumbTeam();

	useEffect(() => {
		// If the user opens a entity page from this context we want the team
		// breadcrumb to match the team of this page.
		if (teamId) {
			setTeamBreadcrumb(teamId);
		}
	}, [setTeamBreadcrumb, teamId]);

	const { data: defaultTemplates } = useTemplateList({
		filters: {
			is_default: true,
		},
	});

	useKeyPress(GLOBAL_SHORTCUTS.SPOTLIGHT_PALETTE, () => {
		spotlight.open();
	});

	const handleCreateQuestionClick = useCallback(async () => {
		entityModalStore.open({
			type: EntityType.question,
			teamId,
			template: defaultTemplates?.results?.[0],
			metadata: {
				owners: [user.id],
			},
		});
	}, [defaultTemplates?.results, teamId, user]);

	const buttons: EmptyStateButtons = useMemo(
		() => [
			{
				label: 'Learn more',
				variant: 'default',
				onClick: () => {
					window.open(
						'https://docs.secoda.com/features/ask-questions-in-secoda',
						'_blank'
					);
				},
			},
			{
				label: 'Create question',
				variant: 'primary',
				onClick: handleCreateQuestionClick,
			},
		],
		[handleCreateQuestionClick]
	);

	const handleMetricCardClick = useCallback(
		(metricType: MetricCardType) => {
			const filters = METRIC_FILTERS[metricType];
			if (!filters) return;

			navigate(
				`/teams/${teamId}/questions?filters=${encodeURIComponent(JSON.stringify(filters))}`
			);
			setActiveTab(`/teams/${teamId}/questions`);
			trackEvent(
				'questions/overview/metric_card/click',
				{
					metric_type: metricType,
				},
				user,
				workspace!
			);
		},
		[navigate, setActiveTab, user, workspace, teamId]
	);

	if (!questionsEnabled) {
		return (
			<EmptyState
				heading="Questions are not enabled"
				body="Please contact your administrator to enable Questions"
				illustrationName="upgrade"
			/>
		);
	}

	if (emptyState?.is_empty) {
		return (
			<EmptyState
				heading="Welcome to Questions"
				body="Foster collaboration and communication around data. Ask questions and share insights to make data-driven decisions"
				illustrationName="questions"
				buttons={buttons}
			/>
		);
	}

	if (questionsOverview) {
		return (
			<FeatureGuard
				featureName="Questions"
				description="Foster collaboration and communication around data. Ask questions and share insights to make data-driven decisions"
				v1AllowedRoles={[
					UserRole.ADMIN,
					UserRole.EDITOR,
					UserRole.VIEWER,
					UserRole.GUEST,
				]}
				v2Permission="Questions.Read"
				isFeatureIncludedInPlan={true}
				isAlwaysIncludedFeature={true}
			>
				<Stack className={classes.wrapper}>
					<Tabs
						value={activeTab}
						onTabChange={handleTabChange}
						keepMounted={false}
					>
						<TabsList mb="sm" tabs={tabs} />
						<Tabs.Panel value={`/teams/${teamId}/questions/overview`}>
							<QuestionsOverviewTabController
								teamId={teamId as string}
								onMetricCardClick={handleMetricCardClick}
							/>
						</Tabs.Panel>
						<Tabs.Panel value={`/teams/${teamId}/questions`}>
							<TeamQuestionsTable />
						</Tabs.Panel>
					</Tabs>
				</Stack>
			</FeatureGuard>
		);
	}

	return (
		<FeatureGuard
			featureName="Questions"
			description="Foster collaboration and communication around data. Ask questions and share insights to make data-driven decisions"
			v1AllowedRoles={ALLOWED_ROLES}
			v2Permission="Questions.Read"
			isFeatureIncludedInPlan={true}
			isAlwaysIncludedFeature={true}
		>
			<Stack className={classes.wrapper}>
				<TeamQuestionsTable />
			</Stack>
		</FeatureGuard>
	);
}

const TeamQuestionsPageWithObserver = observer(TeamQuestionsPage);
export default TeamQuestionsPageWithObserver;
